/// <amd-module name="TFS/Admin/Views/ProfileSettings/ThemeView/ThemeView" />
import React = require("react");

import * as ThemeResources from "TFS/Admin/Views/ProfileSettings/ThemeView/Resources";

import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { isIE } from "TFS/Admin/Common/Util/BrowserUtil";
import { ThemeTile } from "TFS/Admin/Views/ProfileSettings/ThemeView/ThemeTile/ThemeTile";
import { ITfsThemeService, IThemeChangedEvent } from "TFS/ThemeManagement/TfsThemeService";
import { Components, css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ClientTheme } from "VSS/Platform/Theming";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";

interface IThemeComponentState {
    themeGroups?: { [groupName: string]: ClientTheme[] };
    currentThemeId?: string;
    additionalThemesUrl?: string;
}

function sanitizeThemeId(id: string) {
    return id.replace(/\./g, "-");
}

export class ThemeComponent extends VssComponent<{}, IThemeComponentState> {
    private themeService: ITfsThemeService;
    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.themeService = this.context.pageContext.getService<ITfsThemeService>("ITfsThemeService");
        this.themeService.subscribe(this._onSelectedThemeChanged, "selectedThemeChanged");

        this.state = {};
    }

    public componentDidMount(): void {
        super.componentDidMount();

        // Render the feature panel with the initial values.
        this.themeService.getThemes().then(themes => {
            this.setThemes(themes);
        });

        this.themeService.getCurrentThemeId().then(currentThemeId => {
            this.setState({ currentThemeId });
        });

        // TODO: Re-enable this once we support third-party theme extensions
        // this.themeService.getBrowseMoreUrl().then(browseUrl => {
        //    this.setState({ additionalThemesUrl: browseUrl })
        // });

        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.ProfileTheme);
    }

    public render(): JSX.Element {
        if (isIE()) {
            return (
                <div className="flex-column theme-view">
                    <MessageBar className="message-bar" severity={MessageBarSeverity.Error}>
                        {ThemeResources.IEError}
                    </MessageBar>
                </div>
            );
        }

        const { currentThemeId } = this.state;

        const themeGroups = this.state.themeGroups || {};
        const themeGroupNames = Object.keys(themeGroups);

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <div className="flex-column flex-grow theme-view">
                        <h2 className="title-l">{ThemeResources.Title}</h2>
                        {currentThemeId && themeGroupNames.length > 0 && (
                            <FocusZone
                                defaultActiveElement={`#theme-${sanitizeThemeId(currentThemeId)}`}
                                direction={FocusZoneDirection.Horizontal}
                                focusOnMount={true}
                                focusGroupProps={{ defaultElementId: `theme-${sanitizeThemeId(currentThemeId)}` }}
                            >
                                <div>
                                    {themeGroupNames.map(groupName => {
                                        return (
                                            <div className="theme-management-group" key={groupName}>
                                                {themeGroupNames.length > 1 && <p>{groupName}</p>}
                                                <div className="theme-management-container flex-row">
                                                    {themeGroups[groupName].map(theme => (
                                                        <ThemeTile
                                                            key={theme.id}
                                                            selected={theme.id === currentThemeId}
                                                            theme={theme}
                                                            onThemeSelected={this._onThemeSelected}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {this.state.additionalThemesUrl && (
                                        <Link aria-label={ThemeResources.BrowseMoreLink} href={this.state.additionalThemesUrl} target="_blank">
                                            {ThemeResources.BrowseMoreLink}
                                        </Link>
                                    )}
                                </div>
                            </FocusZone>
                        )}
                    </div>
                </Page>
            </Surface>
        );
    }

    private setThemes(themes: ClientTheme[]): void {
        const themeGroups: { [groupName: string]: ClientTheme[] } = {};

        themes = this.addThemesToGroup(
            themes,
            themeGroups,
            ThemeResources.DefaultThemesText,
            t => t.id === "ms.vss-web.vsts-theme-dark" || t.id === "ms.vss-web.vsts-theme"
        );
        themes = this.addThemesToGroup(themes, themeGroups, ThemeResources.CustomThemesText, t => !t.isPreview);
        themes = this.addThemesToGroup(themes, themeGroups, ThemeResources.PreviewThemesText, t => !!t.isPreview);

        this.setState({ themeGroups });
    }

    private addThemesToGroup(
        themes: ClientTheme[],
        themeGroups: { [groupName: string]: ClientTheme[] },
        groupName: string,
        filter: (theme: ClientTheme) => boolean
    ): ClientTheme[] {
        const remainingThemes: ClientTheme[] = [];
        const matchingThemes: ClientTheme[] = [];

        for (const theme of themes) {
            if (filter(theme)) {
                matchingThemes.push(theme);
            } else {
                remainingThemes.push(theme);
            }
        }

        if (matchingThemes.length) {
            themeGroups[groupName] = matchingThemes;
        }

        return remainingThemes;
    }

    private _onSelectedThemeChanged = (themeChangedEvent: IThemeChangedEvent): void => {
        this.setState({ currentThemeId: themeChangedEvent.selectedTheme.id });
    };

    private _onThemeSelected = (theme: ClientTheme): void => {
        this.themeService.setTheme(theme);
    };
}

Components.add("ThemeView", ThemeComponent);
